/**
 * PerformanceTrail — Zero-Dependency Timing & Error-Enrichment Engine
 * 
 * Adapted for Fr00t from the AstroMonix PerformanceTrail architecture.
 * 
 * DESIGN PRINCIPLES:
 * 1. Zero-cost when disabled — single boolean short-circuit
 * 2. Aggregation always runs — even when console output suppressed
 * 3. Budgets warn, never throw — over-budget spans get ⚠️ styling, no interruption
 * 4. History is bounded — ring buffer capped at 200, no memory leaks
 * 5. Per-label throttle, not global — animation-frame at 3s, others at 500ms
 * 6. Over-budget always surfaces — dedicated 1s throttle for violations
 * 7. Single output channel — everything to console.error for filtering
 * 8. Fail-safe activation — all activation wrapped in try/catch
 * 9. No dependencies — self-contained, works in any JS environment
 * 10. Dual-publish — console.error + debugConsole if available
 * 
 * FR00T BUDGETS (ms) — calibrated to the cymatic/3D/audio pipeline:
 *   cymatic-hologram-render  : 33   (30fps target)
 *   cymatic-physics-update   : 24   (physics tick)
 *   audio-engine-init        : 3000 (Web Audio context + node graph)
 *   audio-buffer-update      : 10   (per-frame analyser update)
 *   network-request          : 5000 (API calls)
 *   loading-orchestrator     : 8000 (full app init)
 *   storage-read             : 50
 *   storage-write            : 50
 *   component-render         : 16   (60fps target)
 *   numerology-calculate     : 100
 *   sacred-geometry-render   : 50
 */

export interface SpanEntry {
  startTime: number;
  threshold: number;
}

export interface HistoryEntry {
  label: string;
  elapsed: number;
  timestamp: number;
  overBudget: boolean;
}

export interface Aggregate {
  count: number;
  total: number;
  min: number;
  max: number;
  last: number;
}

export interface PerfDump {
  aggregates: Record<string, Aggregate>;
  history: HistoryEntry[];
  activeSpans: string[];
  budgets: Record<string, number>;
  timestamp: string;
}

declare global {
  interface Window {
    PerfTrail?: PerformanceTrail;
    PerformanceTrail?: PerformanceTrail;
  }
}

class PerformanceTrail {
  // ─── Master Gate ────────────────────────────────────────────
  private _enabled: boolean = false;

  // ─── Active Spans ───────────────────────────────────────────
  private _spans: Record<string, SpanEntry> = {};

  // ─── Ring Buffer History (capped) ───────────────────────────
  private _history: HistoryEntry[] = [];
  private readonly _maxHistory: number = 200;

  // ─── Per-Label Aggregates ───────────────────────────────────
  private _aggregates: Record<string, Aggregate> = {};

  // ─── Budget Thresholds (ms) ─────────────────────────────────
  private _budgets: Record<string, number> = {};

  // ─── Throttle State ─────────────────────────────────────────
  private _lastLogged: Record<string, number> = {};
  private _logThrottleMs: Record<string, number> = {
    '_default': 500,
    'cymatic-hologram-render': 3000,
    'audio-buffer-update': 3000,
    'component-render': 2000,
    '__overbudget__': 1000,
  };

  // ─── FPS Tracking ───────────────────────────────────────────
  private _frameCount: number = 0;
  private _frameWindowStart: number = 0;
  private _fps: number = 0;

  // ─── Singleton ──────────────────────────────────────────────
  private static _instance: PerformanceTrail | null = null;

  private constructor() {
    this.autoEnable();
  }

  public static getInstance(): PerformanceTrail {
    if (!PerformanceTrail._instance) {
      PerformanceTrail._instance = new PerformanceTrail();
    }
    return PerformanceTrail._instance;
  }

  // ═══════════════════════════════════════════════════════════════
  // ACTIVATION
  // ═══════════════════════════════════════════════════════════════

  private autoEnable(): void {
    try {
      // Auto-enable on localhost / dev
      const host = window.location.hostname;
      if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) {
        this._enabled = true;
        return;
      }

      // URL param overrides
      const params = new URLSearchParams(window.location.search);
      if (params.get('debug_perf') === '1' || params.get('debug') === '1') {
        this._enabled = true;
        return;
      }
      if (params.get('debug_perf') === '0') {
        this._enabled = false;
        return;
      }

      // localStorage persistence
      try {
        const stored = localStorage.getItem('fr00t-perf-trail');
        if (stored === '1') { this._enabled = true; return; }
        if (stored === '0') { this._enabled = false; return; }
      } catch (_) { /* sandboxed */ }

      // Development build detection
      if (process.env.NODE_ENV === 'development') {
        this._enabled = true;
      }
    } catch (_) { /* fail-safe: disable */ }
  }

  public enable(): void { this._enabled = true; }
  public disable(): void { this._enabled = false; }
  public isEnabled(): boolean { return this._enabled; }

  // ═══════════════════════════════════════════════════════════════
  // BUDGETS
  // ═══════════════════════════════════════════════════════════════

  public budget(label: string, ms: number): void {
    this._budgets[label] = ms;
  }

  public setThrottle(label: string, ms: number): void {
    this._logThrottleMs[label] = ms;
  }

  // ═══════════════════════════════════════════════════════════════
  // SPAN: START / END
  // ═══════════════════════════════════════════════════════════════

  /**
   * Start timing a span. Zero-cost when _enabled is false.
   */
  public start(label: string): void {
    if (!this._enabled) return;
    this._spans[label] = {
      startTime: performance.now(),
      threshold: this._budgets[label] ?? Infinity,
    };
  }

  /**
   * End timing a span. Returns elapsed ms. Always updates aggregates.
   */
  public end(label: string): number {
    const span = this._spans[label];
    if (!span) return 0;
    delete this._spans[label];

    const elapsed = performance.now() - span.startTime;
    const overBudget = elapsed > span.threshold;

    // Always aggregate
    this.aggregate(label, elapsed);

    // Push to history ring buffer
    this._history.push({
      label,
      elapsed: Math.round(elapsed * 100) / 100,
      timestamp: Date.now(),
      overBudget,
    });
    if (this._history.length > this._maxHistory) {
      this._history.shift();
    }

    // Throttled console output
    if (this._shouldLog(label, overBudget)) {
      this.logSpan(label, elapsed, span.threshold, overBudget);
    }

    // Feed debug panel if available
    this.feedDebugConsole(label, elapsed, overBudget);

    return elapsed;
  }

  // ═══════════════════════════════════════════════════════════════
  // WRAP (sync + async)
  // ═══════════════════════════════════════════════════════════════

  public wrap<T>(label: string, fn: () => T): T;
  public wrap<T>(label: string, fn: () => Promise<T>): Promise<T>;
  public wrap<T>(label: string, fn: () => T | Promise<T>): T | Promise<T> {
    this.start(label);
    try {
      const result = fn();
      if (result instanceof Promise) {
        return result.then(
          (val) => { this.end(label); return val; },
          (err) => { this.end(label); throw err; }
        ) as Promise<T>;
      }
      this.end(label);
      return result;
    } catch (err) {
      this.end(label);
      throw err;
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // ERROR ENRICHMENT
  // ═══════════════════════════════════════════════════════════════

  public error(source: string, message: string, context?: Record<string, unknown>): void {
    if (!this._enabled) return;

    const ctxStr = context
      ? Object.entries(context)
          .map(([k, v]) => {
            const val = typeof v === 'object' ? JSON.stringify(v) : String(v);
            return `${k}=${val}`;
          })
          .join(', ')
      : '';

    const output = ctxStr
      ? `❌ [${source}] ${message} | ${ctxStr}`
      : `❌ [${source}] ${message}`;

    console.error(
      `%c❌%c [${source}] %c${message}%c ${ctxStr ? '| ' + ctxStr : ''}`,
      'font-size:14px;',
      'color:#f472b6;',    // pink source
      'color:#fff;font-weight:bold;',  // white bold message
      'color:#94a3b8;',    // grey context
    );

    // Feed debug panel
    try {
      (window as any).debugConsole?.log('error', `❌ [${source}] ${message}${ctxStr ? ' | ' + ctxStr : ''}`);
    } catch (_) { /* no debug panel */ }
  }

  // ═══════════════════════════════════════════════════════════════
  // CHECKPOINT
  // ═══════════════════════════════════════════════════════════════

  public checkpoint(label: string, data?: Record<string, unknown>): void {
    if (!this._enabled) return;

    const ctxStr = data
      ? Object.entries(data)
          .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`)
          .join(', ')
      : '';

    console.error(
      `%c◆%c ${label}%c${ctxStr ? ' | ' + ctxStr : ''}`,
      'color:#c084fc;',     // purple diamond
      'color:#c084fc;',     // purple label
      'color:#94a3b8;',    // grey context
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // FPS TRACKING
  // ═══════════════════════════════════════════════════════════════

  public tick(): number {
    if (!this._enabled) return 0;

    const now = performance.now();
    if (this._frameWindowStart === 0) {
      this._frameWindowStart = now;
    }

    this._frameCount++;

    const elapsed = now - this._frameWindowStart;
    if (elapsed >= 1000) {
      this._fps = Math.round((this._frameCount * 1000) / elapsed);
      this._frameCount = 0;
      this._frameWindowStart = now;

      if (this._fps < 45 && this._shouldLog('__fps__', true)) {
        console.error(
          `%c○ %cFPS: ${this._fps}%c (below 45)`,
          'color:#f87171;',
          'color:#f87171;font-weight:bold;',
          'color:#94a3b8;',
        );
      }
    }

    return this._fps;
  }

  public fps(): number {
    return this._fps;
  }

  // ═══════════════════════════════════════════════════════════════
  // REPORTING
  // ═══════════════════════════════════════════════════════════════

  public report(): Array<{ label: string; count: number; last_ms: number; avg_ms: number; min_ms: number; max_ms: number; total_ms: number }> | null {
    const entries = Object.entries(this._aggregates);
    if (entries.length === 0) return null;

    const rows = entries.map(([label, agg]) => ({
      label,
      count: agg.count,
      last_ms: Math.round(agg.last * 100) / 100,
      avg_ms: Math.round((agg.total / agg.count) * 100) / 100,
      min_ms: Math.round(agg.min * 100) / 100,
      max_ms: Math.round(agg.max * 100) / 100,
      total_ms: Math.round(agg.total * 100) / 100,
    }));

    console.table(rows);
    return rows;
  }

  public dump(): PerfDump {
    const payload: PerfDump = {
      aggregates: { ...this._aggregates },
      history: this._history.slice(-100),
      activeSpans: Object.keys(this._spans),
      budgets: { ...this._budgets },
      timestamp: new Date().toISOString(),
    };

    console.error(JSON.stringify(payload, null, 2));

    try {
      navigator.clipboard?.writeText(JSON.stringify(payload, null, 2));
    } catch (_) { /* clipboard unavailable */ }

    return payload;
  }

  public reset(): void {
    this._spans = {};
    this._history = [];
    this._aggregates = {};
    this._lastLogged = {};
    this._frameCount = 0;
    this._frameWindowStart = 0;
    this._fps = 0;
  }

  // ═══════════════════════════════════════════════════════════════
  // INTERNAL HELPERS
  // ═══════════════════════════════════════════════════════════════

  private aggregate(label: string, elapsed: number): void {
    const agg = this._aggregates[label] || { count: 0, total: 0, min: Infinity, max: 0, last: 0 };
    agg.count++;
    agg.total += elapsed;
    agg.min = Math.min(agg.min, elapsed);
    agg.max = Math.max(agg.max, elapsed);
    agg.last = elapsed;
    this._aggregates[label] = agg;
  }

  private _shouldLog(label: string, overBudget: boolean): boolean {
    const now = Date.now();
    const key = overBudget ? '__overbudget__' : label;
    const last = this._lastLogged[key] || 0;
    const throttle = this._logThrottleMs[key] ?? this._logThrottleMs['_default'] ?? 500;

    if (now - last < throttle) return false;
    this._lastLogged[key] = now;
    return true;
  }

  private logSpan(label: string, elapsed: number, threshold: number, overBudget: boolean): void {
    const agg = this._aggregates[label];
    const avg = agg ? Math.round((agg.total / agg.count) * 100) / 100 : elapsed;
    const icon = overBudget ? '⚠️' : '⏱️';
    const color = overBudget ? '#fb923c' : '#6cb4ee';

    const budgetStr = threshold < Infinity ? ` / ${threshold}ms budget` : '';
    const statsStr = agg && agg.count > 1
      ? ` (avg:${avg}ms, min:${Math.round(agg.min * 100) / 100}ms, max:${Math.round(agg.max * 100) / 100}ms)`
      : '';

    console.error(
      `%c${icon}%c ${label}: %c${Math.round(elapsed * 100) / 100}ms%c${budgetStr}%c${statsStr}`,
      'font-size:14px;',
      `color:${color};`,
      `color:${color};font-weight:bold;`,
      `color:#94a3b8;`,
      `color:#64748b;`,
    );
  }

  private feedDebugConsole(label: string, elapsed: number, overBudget: boolean): void {
    try {
      const flag = overBudget ? ' ⚠️' : '';
      (window as any).debugConsole?.log('perf', `[perf] ${label}: ${Math.round(elapsed * 100) / 100}ms${flag}`);
    } catch (_) { /* no debug panel */ }
  }
}

// ─── Singleton Export ──────────────────────────────────────────
export const perfTrail = PerformanceTrail.getInstance();

// ─── Fr00t Budget Definitions (ms) — calibrated to the audio/3D/numerology pipeline ──
perfTrail.budget('loading-orchestrator', 8000);
perfTrail.budget('cymatic-hologram-render', 33);
perfTrail.budget('cymatic-physics-update', 24);
perfTrail.budget('audio-engine-init', 3000);
perfTrail.budget('audio-buffer-update', 10);
perfTrail.budget('network-request', 5000);
perfTrail.budget('storage-read', 50);
perfTrail.budget('storage-write', 50);
perfTrail.budget('component-render', 16);
perfTrail.budget('numerology-calculate', 100);
perfTrail.budget('sacred-geometry-render', 50);
perfTrail.budget('animation-frame', 33);
perfTrail.budget('fps', 5000);
perfTrail.budget('auth-login', 5000);
perfTrail.budget('auth-register', 5000);
perfTrail.budget('audio-play-scale', 500);
perfTrail.budget('audio-play-chord', 500);

// Per-label throttle tuning
perfTrail.setThrottle('cymatic-hologram-render', 3000);
perfTrail.setThrottle('animation-frame', 3000);
perfTrail.setThrottle('audio-buffer-update', 3000);
perfTrail.setThrottle('component-render', 2000);

console.log(
  '%c🔬%c PerformanceTrail %cready %c| %c' + 
  (perfTrail.isEnabled() ? 'ENABLED' : 'DISABLED') + '%c | %cFr00t budgets calibrated',
  'font-size:14px;',
  'color:#c084fc;',
  'color:#94a3b8;',
  'color:#64748b;',
  'color:' + (perfTrail.isEnabled() ? '#4ade80' : '#f87171') + ';',
  'color:#64748b;',
  'color:#94a3b8;'
);

// ─── Global Window Export ──────────────────────────────────────
if (typeof window !== 'undefined') {
  window.PerfTrail = perfTrail;
  window.PerformanceTrail = perfTrail;
}

export default perfTrail;
